import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import Navbar from "../../components/portal/Navbar";
import Footer from "../../components/portal/Footer";
import WhatsAppBtn from "../../components/portal/WhatsAppBtn";
import PropertyCard from "../../components/portal/PropertyCard";
import GaleriaPropiedad from "../../components/portal/GaleriaPropiedad";
import FormasPagoChips from "../../components/portal/FormasPagoChips";
import { telefonoValido, emailValido } from "../../constants/telefono";
import { portalAPI } from "../../api";
import { formatPrecio, getLabelTipo, AMENIDADES } from "../../utils";
import { useSettings } from "../../context/SettingsContext";
import toast from "react-hot-toast";
import "./Detalle.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Mensaje precargado: incluye la clave/ID si la propiedad la tiene
const mensajeInteres = (p) =>
    `Hola, me interesa la propiedad: ${p.title}${p.custom_id ? ` (Clave: ${p.custom_id})` : ""}`;

// Para WhatsApp: el mismo mensaje + el enlace limpio de la propiedad en su propia línea
// (sin ?utm ni #, y al final para que WhatsApp muestre la tarjeta con foto)
const mensajeWhatsApp = (p) =>
    `${mensajeInteres(p)}\n${window.location.origin}/propiedades/${p.slug}`;

export default function Detalle() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [propiedad, setPropiedad] = useState(null);
  const [similares, setSimilares] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", message: "",
    payment_methods: [], payment_other: "",
  });
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCargando(true);
    portalAPI.detalle(slug)
        .then((r) => {
          setPropiedad(r.data.data);
          setForm((f) => ({ ...f, message: mensajeInteres(r.data.data) }));
          portalAPI.similares(slug).then((s) => setSimilares(s.data.data)).catch(() => {});
        })
        .catch(() => setPropiedad(null))
        .finally(() => setCargando(false));
  }, [slug]);

  const handleContacto = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Escribe tu nombre"); return; }
    if (!telefonoValido(form.phone)) { toast.error("Escribe un teléfono de 10 dígitos"); return; }
    if (form.email.trim() && !emailValido(form.email)) { toast.error("El correo no es válido"); return; }
    setEnviando(true);
    try {
      const esVenta = propiedad.operation === "VENTA";
      await portalAPI.contacto({
        ...form,
        property_id: propiedad.id,
        payment_methods: esVenta ? form.payment_methods : [],
        payment_other: esVenta ? form.payment_other : "",
      });
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setForm((f) => ({ ...f, name: "", email: "", phone: "", payment_methods: [], payment_other: "" }));
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar. Intenta de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  const imagenes = propiedad?.images || [];
  // Portada primero, luego en el orden que se acomodó en el admin
  const ordenadas = [...imagenes].sort((a, b) =>
      (b.is_cover === true) - (a.is_cover === true) ||
      (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
      String(a.id).localeCompare(String(b.id))
  );

  if (cargando) return (
      <>
        <Navbar />
        <div style={{ paddingTop: "120px", textAlign: "center" }}>
          <div className="spinner" />
        </div>
      </>
  );

  if (!propiedad) return (
      <>
        <Navbar />
        <div style={{ paddingTop: "120px", textAlign: "center", padding: "6rem 1rem" }}>
          <h2>Propiedad no encontrada</h2>
          <Link to="/propiedades" className="btn btn-primary" style={{ marginTop: "1rem" }}>
            Ver propiedades
          </Link>
        </div>
        <Footer />
      </>
  );

  const {
    title, operation, type, price, currency, show_price, price_negotiable,
    city, neighborhood, state, address, custom_id,
    bedrooms, bathrooms, half_bathrooms, parking_spaces,
    construction_m2, land_m2, floors, year_built, furnished,
    has_cistern, has_water_tank, has_natural_gas, has_electricity,
    has_water, has_drainage, has_internet,
    amenities, description, latitude, longitude, map_zoom,
  } = propiedad;

  const servicios = [
    { label: "Cisterna", val: has_cistern, icon: "💧" },
    { label: "Tinaco", val: has_water_tank, icon: "🪣" },
    { label: "Gas natural", val: has_natural_gas, icon: "🔥" },
    { label: "Electricidad", val: has_electricity, icon: "⚡" },
    { label: "Agua", val: has_water, icon: "🚰" },
    { label: "Drenaje", val: has_drainage, icon: "🪠" },
    { label: "Internet", val: has_internet, icon: "📡" },
  ];

  const WHATSAPP = settings.whatsapp_number || "";

  return (
      <div>
        <Navbar />

        <div className="detalle-page">
          <div className="container">
            {/* Breadcrumb */}
            <div className="breadcrumb">
              <Link to="/">Inicio</Link> ›
              <Link to="/propiedades"> Propiedades</Link> ›
              <span> {title}</span>
            </div>

            {/* ── HEADER con precio a la derecha ── */}
            <div className="detalle-header">
              <div className="detalle-badges">
              <span className={operation === "VENTA" ? "badge-venta" : "badge-renta"}>
                {operation === "VENTA" ? "Venta" : "Renta"}
              </span>
                <span className="badge-tipo">{getLabelTipo(type)}</span>
              </div>

              <div className="detalle-header-top">
                {/* Info izquierda */}
                <div className="detalle-header-info">
                  <h1 className="heading-serif detalle-title">{title}</h1>
                  <p className="detalle-location">
                    📍 {address ? `${address}, ` : ""}{neighborhood ? `${neighborhood}, ` : ""}{city}, {state}
                  </p>
                  {custom_id && <p className="detalle-ref">Ref: {custom_id}</p>}
                </div>

                {/* Precio derecha */}
                <div className="detalle-precio-header">
                  <p className="detalle-precio-etiqueta">
                    {operation === "VENTA" ? "En Venta" : "En Renta"}
                  </p>
                  <p className="detalle-precio">
                    {formatPrecio(price, currency, show_price)}
                  </p>
                  {price_negotiable && show_price && (
                      <span className="badge-featured">💬 Precio a tratar</span>
                  )}
                </div>
              </div>
            </div>

            {/* ── GALERÍA (estilo Airbnb + carrusel en celular + pantalla completa) ── */}
            <GaleriaPropiedad imagenes={ordenadas} title={title} />
          </div>

          {/* ── CONTENIDO ── */}
          <div className="container detalle-body">
            <div className="detalle-main">

              {/* Características */}
              <div className="detalle-section">
                <h2 className="detalle-section-title">Características</h2>
                <div className="caracteristicas-grid">
                  {bedrooms != null && <div className="caract-item"><span className="caract-icon">🛏</span><span className="caract-val">{bedrooms}</span><span className="caract-label">Recámaras</span></div>}
                  {bathrooms != null && <div className="caract-item"><span className="caract-icon">🚿</span><span className="caract-val">{bathrooms}</span><span className="caract-label">Baños</span></div>}
                  {half_bathrooms > 0 && <div className="caract-item"><span className="caract-icon">🚽</span><span className="caract-val">{half_bathrooms}</span><span className="caract-label">Medios baños</span></div>}
                  {parking_spaces != null && <div className="caract-item"><span className="caract-icon">🚗</span><span className="caract-val">{parking_spaces}</span><span className="caract-label">Estacionamiento</span></div>}
                  {construction_m2 != null && <div className="caract-item"><span className="caract-icon">📐</span><span className="caract-val">{construction_m2} m²</span><span className="caract-label">Construcción</span></div>}
                  {land_m2 != null && <div className="caract-item"><span className="caract-icon">🌿</span><span className="caract-val">{land_m2} m²</span><span className="caract-label">Terreno</span></div>}
                  {floors != null && <div className="caract-item"><span className="caract-icon">🏢</span><span className="caract-val">{floors}</span><span className="caract-label">Pisos</span></div>}
                  {year_built != null && <div className="caract-item"><span className="caract-icon">📅</span><span className="caract-val">{year_built}</span><span className="caract-label">Año</span></div>}
                  {furnished && <div className="caract-item"><span className="caract-icon">🛋️</span><span className="caract-val">{furnished === "AMUEBLADO" ? "Sí" : furnished === "SEMI_AMUEBLADO" ? "Semi" : "No"}</span><span className="caract-label">Amueblado</span></div>}
                </div>
              </div>

              {/* Servicios */}
              <div className="detalle-section">
                <h2 className="detalle-section-title">Servicios</h2>
                <div className="servicios-grid">
                  {servicios.map((s) => (
                      <div key={s.label} className={`servicio-item ${s.val ? "yes" : "no"}`}>
                        <span>{s.icon}</span><span>{s.label}</span>
                        <span>{s.val ? "✓" : "—"}</span>
                      </div>
                  ))}
                </div>
              </div>

              {/* Amenidades */}
              {amenities?.length > 0 && (
                  <div className="detalle-section">
                    <h2 className="detalle-section-title">Amenidades</h2>
                    <div className="amenidades-list">
                      {amenities.map((a) => {
                        const info = AMENIDADES.find((x) => x.value === a);
                        return (
                            <span key={a} className="amenidad-chip">
                        {info?.emoji || "✓"} {info?.label || a}
                      </span>
                        );
                      })}
                    </div>
                  </div>
              )}

              {/* Descripción */}
              {description && (
                  <div className="detalle-section">
                    <h2 className="detalle-section-title">Descripción</h2>
                    <div className="detalle-desc" dangerouslySetInnerHTML={{ __html: description }} />
                  </div>
              )}

              {/* Mapa */}
              {latitude && longitude && (
                  <div className="detalle-section">
                    <h2 className="detalle-section-title">Ubicación</h2>
                    <div style={{ height: 320, borderRadius: "var(--radius-lg)", overflow: "hidden", position: "relative", zIndex: 0 }}>
                      <MapContainer
                          center={[parseFloat(latitude), parseFloat(longitude)]}
                          zoom={map_zoom || 15}
                          style={{ height: "100%", width: "100%" }}
                          scrollWheelZoom={false}
                      >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[parseFloat(latitude), parseFloat(longitude)]}>
                          <Popup>{title}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                    <a href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                       target="_blank" rel="noreferrer"
                       className="btn btn-outline btn-sm" style={{ marginTop: ".75rem" }}>
                      🗺️ Ver en Google Maps
                    </a>
                  </div>
              )}
            </div>

            {/* Aside — contacto */}
            <aside className="detalle-aside">
              <div className="contacto-box card">
                <h3>¿Te interesa esta propiedad?</h3>
                <p>Escríbenos y te contactamos a la brevedad.</p>
                <form onSubmit={handleContacto} className="contacto-form">
                  <div className="form-group">
                    <label className="form-label" htmlFor="det-nombre">Nombre *</label>
                    <input id="det-nombre" className="form-input" value={form.name} autoComplete="name"
                           onChange={(e) => setForm({ ...form, name: e.target.value })}
                           placeholder="Tu nombre" />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="det-tel">Teléfono / WhatsApp *</label>
                    <input id="det-tel" type="tel" inputMode="tel" autoComplete="tel" className="form-input"
                           value={form.phone}
                           onChange={(e) => setForm({ ...form, phone: e.target.value })}
                           placeholder="10 dígitos, ej. 271 123 4567" />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="det-email">
                      Email <span style={{ fontWeight: 400, fontSize: ".8rem", color: "var(--color-text-muted)" }}>(opcional)</span>
                    </label>
                    <input id="det-email" type="email" autoComplete="email" className="form-input" value={form.email}
                           onChange={(e) => setForm({ ...form, email: e.target.value })}
                           placeholder="tu@email.com" />
                  </div>
                  {operation === "VENTA" && (
                      <FormasPagoChips
                          seleccionadas={form.payment_methods}
                          otro={form.payment_other}
                          onChange={(lista) => setForm((f) => ({ ...f, payment_methods: lista }))}
                          onOtroChange={(texto) => setForm((f) => ({ ...f, payment_other: texto }))}
                      />
                  )}
                  <div className="form-group">
                    <label className="form-label">Mensaje</label>
                    <textarea className="form-textarea" value={form.message}
                              onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={enviando}>
                    {enviando ? "Enviando..." : "📩 Enviar consulta"}
                  </button>
                  {WHATSAPP && (
                      <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(mensajeWhatsApp(propiedad))}`}
                         target="_blank" rel="noreferrer"
                         className="btn btn-sm"
                         style={{ width: "100%", background: "#25D366", color: "#fff", justifyContent: "center", display: "flex", marginTop: ".5rem" }}>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{ flexShrink: 0 }} aria-hidden="true">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg> Contactar por WhatsApp
                      </a>
                  )}
                </form>
              </div>

              <div className="compartir-box card">
                <h4>Compartir propiedad</h4>
                <div className="compartir-btns">
                  <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success("¡Enlace copiado!"); }}
                          className="btn btn-outline btn-sm">🔗 Copiar enlace</button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`${title} - ${window.location.href}`)}`}
                     target="_blank" rel="noreferrer"
                     className="btn btn-sm"
                     style={{ background: "#25D366", color: "#fff", display: "inline-flex", alignItems: "center", gap: ".4rem" }}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{ flexShrink: 0 }} aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp
                  </a>
                </div>
              </div>
            </aside>
          </div>

          {/* Similares */}
          {similares.length > 0 && (
              <div className="container similares-section">
                <h2 className="heading-serif">También podría interesarte</h2>
                <div className="similares-grid">
                  {similares.map((p) => <PropertyCard key={p.id} propiedad={p} />)}
                </div>
              </div>
          )}
        </div>
        <Footer />
        <WhatsAppBtn mensaje={mensajeWhatsApp(propiedad)} />
      </div>
  );
}